import { useRef, useMemo, useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import ReactQuill from "react-quill";
import PropTypes from "prop-types";
import { Box } from '@mui/material';
import { StyledEditor } from '../../../../../reusable-components/comment-editor/styles';
import AIAssistButton from '../../../../../reusable-components/editor/AIAssistButton';
import { handleSetVerticalPosition } from '../../../../../reusable-components/editor/handlePosition';
import 'quill-mention';
import 'quill-mention/dist/quill.mention.css';

MobileTicketInputEditor.propTypes = {
  message: PropTypes.string,
  setMessage: PropTypes.func,
  prependText: PropTypes.object,
}
export default function MobileTicketInputEditor({ message, setMessage, prependText }) {
  const { id = '' } = useParams();
  const location = useLocation();
  const subticketId = new URLSearchParams(location.search).get('subticket');
  const { users } = useSelector((state) => state.tickets.data.ticketInfoById[subticketId || id]) || [];
  const quillRef = useRef(null);

  const [cursorVerticalPosition, setCursorVerticalPosition] = useState(0);

  const mentionSource = () => (searchTerm, renderList) => {
    if (searchTerm?.length > 0) {
      const matches = [];
      for (let i = 0; i < users?.length; i++) {
        const nameParts = users[i].value.split(' ');
        const matchFound = nameParts.some((part) => part.toLowerCase().startsWith(searchTerm.toLowerCase()));
        if (matchFound) {
          matches.push(users[i]);
          if (matches.length >= 5) break;
        }
      }
      renderList(matches, searchTerm);
    }
  };
  
  const modules = useMemo(() => ({
    toolbar: false,
    history: {
      delay: 500,
      maxStack: 100,
      userOnly: true,
    },
    syntax: true,
    clipboard: {
      matchVisual: false,
    },
    mention: {
      allowedChars: /^[A-Za-z\sÅÄÖåäö]*$/,
      isolateCharacter: true,
      mentionDenotationChars: ['@'],
      spaceAfterInsert: true,
      defaultMenuOrientation: 'top',
      positioningStrategy: 'fixed',
      showDenotationChar: false,
      source: mentionSource(),
    },
  }), []);

  const handleScrollInputIntoView = () => {
    setTimeout(() => {
      const editor = quillRef.current && quillRef.current.getEditor && quillRef.current.getEditor().root;
      if (editor) {
        editor.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
    }, 300);
  };

  useEffect(() => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return undefined;

    const updateVerticalPosition = (range) => {
      const position = handleSetVerticalPosition(quill, range);
      setCursorVerticalPosition(position);
    };

    const handleTextChange = () => {
      const range = quill.getSelection();
      updateVerticalPosition(range);
    };

    quill.on('text-change', handleTextChange);
    quill.on('selection-change', updateVerticalPosition);

    // Cleanup
    return () => {
      quill.off('text-change', handleTextChange);
      quill.off('selection-change', updateVerticalPosition);
    };
  }, []);

  useEffect(() => {
    if (!prependText?.text) return;
    const quill = quillRef.current?.getEditor();
    if (!quill) return;
    quill.insertText(0, `${prependText.text}\n`, 'user');
    quill.setSelection(0, 0, 'user');
  }, [prependText]);

  return <Box sx={{ position: 'relative', width: '100%', paddingLeft: 3}}>
    <AIAssistButton
      cursorVerticalPosition={cursorVerticalPosition}
      value={message}
      quillRef={quillRef}
    />
    <StyledEditor
    width='100%'
    sx={{
      border: 'none',
      minHeight: '32px',
      maxHeight: '200px',
      '& .ql-container': {
        minHeight: '32px',
        maxHeight: '200px',
        border: 'none',
      },
      '& .ql-editor': {
        minHeight: '32px',
        maxHeight: '200px',
        padding: '4px',
        mb: 0,
        width: '100%',
        wordBreak: 'break-word'
      },
    }}>
    <ReactQuill
      ref={quillRef}
      value={message}
      onChange={(e) => setMessage(e)}
      modules={modules}
      onFocus={handleScrollInputIntoView}
    />
  </StyledEditor>
  </Box>
}